import React, { useEffect, useMemo, useState, memo } from 'react';
import {
  FormGroup,
  ControlLabel,
  Badge,
  FormControl,
  Collapse,
  InputGroup,
} from 'react-bootstrap';
import './_pages.form.scss'
import './_pages.form.dark-mode.scss'
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Translations from '../../../../../../components/Collapse/Translations';
import { getCurrentDomain } from '../../../../../../utils/helper';
import Summernote from '../../../../../../components/Summernote';
import Seo from '../../../../../../components/Seo';
import { summernoteOptions } from '../../helpers';

const PagesForm = ({ page, default_language, isLoading, languages, isCreatePage, onChange }) => {
  const [formData, setFormData] = useState(page)
  const [isOpenTranslations, setOpenTranslations] = useState(false);

  const isPublic = formData.is_public ? 1 : 0;
  const { access } = formData;
  const domain = useMemo(() => getCurrentDomain(), []);
  const defaultLanguage = useMemo(() => languages.find((language) => language.code === default_language), [default_language, languages])

  const anotherLanguages = useMemo(() => languages.filter((language) => language.code !== default_language), [default_language, languages])

  const onChangeUrl = (value) => {
    const url = value
      .toLowerCase()
      .trim()
      .replace(/\s|_+/g, '')
    setFormData((prevState) => ({
      ...prevState,
      url,
    }))
  }

  const onChangePageName = (value, languageCode) => {
    setFormData((prevState) => ({
      ...prevState,
      page_name: {
        ...prevState.page_name,
        [languageCode]: value,
      },
    }))
    /* Если создаем страницу, автоматически создаем url из названия */
    if (languageCode === default_language && isCreatePage) {
      onChangeUrl(value)
    }
  }

  const onChangeSeo = (value, languageCode) => {
    setFormData((prevState) => ({
      ...prevState,
      seo_title: {
        ...prevState.seo_title,
        [languageCode]: value.title,
      },
      seo_keywords: {
        ...prevState.seo_keywords,
        [languageCode]: value.keywords,
      },
      seo_description: {
        ...prevState.seo_description,
        [languageCode]: value.description,
      },
    }))
  }

  const onChangePublic = (value) => {
    const valueToNumber = Number(value);
    setFormData((prevState) => ({
      ...prevState,
      is_public: !!valueToNumber,
    }))
  }

  const onChangeContent = (value, languageCode) => {
    setFormData((prevState) => ({
      ...prevState,
      page_content: {
        ...prevState.page_content,
        [languageCode]: value,
      },
    }))
  }

  useEffect(() => {
    onChange(formData)
  }, [formData, onChange])

  return (
    <div>
      {isLoading ? <div className="modal-loader" /> : null}
      <FormGroup
        className={classNames({
          'form-group__rtl': defaultLanguage.direction === 'rtl',
        })}
      >
        <ControlLabel htmlFor={`page-name-${defaultLanguage.code}`}>
          Page name
          {' '}
          <Badge>{defaultLanguage.title}</Badge>
        </ControlLabel>
        <Translations onOpen={() => setOpenTranslations(!isOpenTranslations)} open={isOpenTranslations} items={languages}>
          <FormControl
            type="text"
            id={`page-name-${defaultLanguage.code}`}
            name={`page_name_${defaultLanguage.code}`}
            value={formData.page_name[defaultLanguage.code]}
            onChange={(e) => onChangePageName(e.target.value, defaultLanguage.code)}
            required
          />
        </Translations>
      </FormGroup>
      {access.content_edit
        ? (
          <FormGroup
            key={`content-${defaultLanguage.code}`}
            className={classNames({
              'form-group__rtl': defaultLanguage.direction === 'rtl',
            })}
          >
            <ControlLabel>
              Content
              {' '}
              <Badge>{defaultLanguage.title}</Badge>
            </ControlLabel>
            <Summernote
              value={formData.page_content[defaultLanguage.code]}
              options={{ ...summernoteOptions, htmlParser: true }}
              onChange={(value) => onChangeContent(value, defaultLanguage.code)}
            />
          </FormGroup>
        ) : null}
      {formData.is_public
        ? (
          <FormGroup>
            <Seo
              title={formData.seo_title[defaultLanguage.code]}
              keywords={formData.seo_keywords[defaultLanguage.code]}
              description={formData.seo_description[defaultLanguage.code]}
              languageName={defaultLanguage.title}
              url={formData.url}
              onChange={(value) => onChangeSeo(value, defaultLanguage.code)}
            />
          </FormGroup>
        ) : null}
      <Collapse in={isOpenTranslations}>
        <div>
          {anotherLanguages.length ? anotherLanguages.map((language) => (
            <div key={`page-translation-${language.code}`}>
              <FormGroup
                className={classNames({
                  'form-group__rtl': language.direction === 'rtl',
                })}
              >
                <ControlLabel htmlFor={`page-name-${language.code}`}>
                  Page name
                  {' '}
                  <Badge>{language.title}</Badge>
                </ControlLabel>
                <FormControl
                  type="text"
                  id={`page-name-${language.code}`}
                  name={`page_name_${language.code}`}
                  value={formData.page_name[language.code]}
                  onChange={(e) => onChangePageName(e.target.value, language.code)}
                  required
                />
              </FormGroup>
              {access.content_edit
                ? (
                  <FormGroup
                    key={`content-${language.code}`}
                    className={classNames({
                      'form-group__rtl': language.direction === 'rtl',
                    })}
                  >
                    <ControlLabel>
                      Content
                      {' '}
                      <Badge>{language.title}</Badge>
                    </ControlLabel>
                    <Summernote
                      value={formData.page_content[language.code]}
                      options={{ ...summernoteOptions, htmlParser: true }}
                      onChange={(value) => onChangeContent(value, language.code)}
                    />
                  </FormGroup>
                ) : null}
              {formData.is_public
                ? (
                  <FormGroup>
                    <Seo
                      title={formData.seo_title[language.code]}
                      keywords={formData.seo_keywords[language.code]}
                      description={formData.seo_description[language.code]}
                      languageCode={language.code}
                      languageName={language.title}
                      url={formData.url}
                      onChange={(value) => onChangeSeo(value, language.code)}
                    />
                  </FormGroup>
                ) : null}
            </div>
          )) : null}
        </div>
      </Collapse>
      {access?.url_edit ? (
        <>
          <div className="pages-form__line" />
          <FormGroup>
            <ControlLabel htmlFor={`page-name-${defaultLanguage.code}`}>
              URL
            </ControlLabel>
            <InputGroup>
              <InputGroup.Addon>
                {domain}
                /
              </InputGroup.Addon>
              <FormControl
                type="text"
                id="url"
                name="url"
                placeholder={formData.default_url || ''}
                value={formData.url}
                onChange={(e) => onChangeUrl(e.target.value)}
              />
            </InputGroup>
          </FormGroup>
        </>
      ) : null }
      {access.public_show
        ? (
          <FormGroup controlId="public">
            <ControlLabel>Public</ControlLabel>
            <FormControl
              disabled={!access.public_change}
              value={isPublic}
              onChange={(e) => onChangePublic(e.target.value)}
              componentClass="select"
              placeholder="select"
            >
              <option value="1">Yes</option>
              <option value="0">No</option>
            </FormControl>
          </FormGroup>
        ) : null}
    </div>
  );
};

const arePropsEqual = (prevProps, nextProps) => nextProps.isLoading === prevProps.isLoading

PagesForm.propTypes = {
  page: PropTypes.object,
  default_language: PropTypes.string,
  isLoading: PropTypes.bool,
  languages: PropTypes.array,
  isCreatePage: PropTypes.bool,
  onChange: PropTypes.func,
};

export default memo(PagesForm, arePropsEqual)
